import { useEffect, useState } from 'hono/jsx';
import { Layout } from '../components/Layout';
import { api } from '../lib/api';
import { showToast } from '../lib/toast';
import { useAuth } from '../lib/auth';

/**
 * Account settings. Currently one section: email notifications. Build-complete
 * emails link here so a user can switch them off from the mail itself.
 */
export function Account() {
  const { user, refresh } = useAuth();
  const [notifyBuild, setNotifyBuild] = useState<boolean>(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setNotifyBuild(user.notifyBuildComplete !== false);
  }, [user?.id, user?.notifyBuildComplete]);

  async function onToggle(next: boolean) {
    const previous = notifyBuild;
    setNotifyBuild(next);
    setSaving(true);
    try {
      const r = await api('/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notifyBuildComplete: next }),
      });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error((err as any).error || 'Failed to save'); }
      await refresh();
      showToast('success', 'Saved', next ? 'We will email you when a build finishes.' : 'Build emails are off.');
    } catch (err: any) {
      setNotifyBuild(previous);
      showToast('error', 'Error', err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Layout active="account" maxWidth="max-w-3xl">
      <h1 class="text-2xl font-bold text-slate-100 mb-1">Account settings</h1>
      <p class="text-slate-400 mb-6">{user?.email}</p>

      <div id="notifications" class="bg-dark-800 border border-slate-700/50 rounded-xl p-6">
        <h2 class="text-lg font-semibold text-slate-100 mb-4">Email notifications</h2>
        <label class="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox" checked={notifyBuild} disabled={saving}
            onChange={(e: Event) => onToggle((e.currentTarget as HTMLInputElement).checked)}
            class="mt-1 w-4 h-4 rounded border-slate-600 bg-dark-700 text-cyan-500 focus:ring-cyan-500/50"
          />
          <span>
            <span class="block text-sm font-medium text-slate-200">Email me when a site build finishes</span>
            <span class="block text-xs text-slate-500 mt-0.5">
              Builds run in the background and can take several minutes, so we let you know when the
              preview is ready instead of making you watch the page.
            </span>
          </span>
        </label>
      </div>
    </Layout>
  );
}
